/**
 * Q277's league menu: every league grouped under its sport heading, where a
 * fan pins, unpins, reorders the pinned set, or jumps straight to a league.
 * Opens from the caret on `All`.
 *
 * ISSUE-907 defect A, and the "pinned list overflows the sheet" half of the
 * card. This sheet used to render two independent lists: a Pinned section
 * carrying only reorder arrows, and, below it, every sport with EVERY league
 * under it. A pinned league was in both, so a fan with eight pins read the
 * same eight names twice, and those eight extra rows, 44px each and 352px of
 * them together, pushed "Football", "Basketball" and the rest below the fold
 * of a sheet capped at 85dvh.
 *
 * Deleting either list was not on offer: the Pinned section is where ORDER is
 * set and the sport sections are where a league is switched ON and OFF, and
 * both jobs have to stay reachable. So the two lists were merged into one row
 * shape instead. `LeagueRow` (LeagueMenuRow.tsx) draws the checkbox, the
 * name, the optional "Coming soon" label and the drill-down arrow, and a
 * pinned row hands it a grip handle as an extra (PinnedLeagueList.tsx,
 * ISSUE-977). `buildLeagueMenuView` then hoists pinned leagues out of their
 * sport section, so a league is drawn exactly once, with every control it
 * had before:
 *
 *   - pin and unpin: the checkbox, on every row in either section
 *   - reorder: the grip handle, on pinned rows, which is the only place an
 *     order exists to change. It drags with a mouse or a finger, and the up
 *     and down arrow keys on the focused handle move a league one place.
 *   - drill down: the chevron, on every row
 *
 * The sheet is now as tall as the catalog rather than the catalog plus the
 * pin list, which is what puts the sport headings back above the fold.
 *
 * TEST IDS, and why they read oddly now. `league-menu-league` is on the `li`
 * of BOTH sections, because a pinned row is a league row too, and
 * `league-menu-pinned-row` sits on the grip handle inside it (ISSUE-977; it
 * sat on the up and down arrow group before). The unit suite,
 * `tests/e2e/src/app/leagueMenuGeometry.e2e.ts` and
 * `tests/e2e/src/support/leagueMenuPage.ts` count `league-menu-pinned-row`
 * to know how many leagues are pinned, so both ids have to survive a pin on
 * the one row that now serves both. Renaming either one is an e2e edit, and
 * that suite needs the full compose stack to run.
 */
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, Dialog, Skeleton } from "@sportsmet/ui";
import { type ReactNode } from "react";

import { LeagueRow } from "./LeagueMenuRow";
import { buildLeagueMenuView } from "./leagueMenuViewModel";
import {
  LEAGUE_MENU_PINNED_HEADING,
  LEAGUE_MENU_TITLE,
  NO_LEAGUES_BODY,
  NO_LEAGUES_TITLE,
  NO_PINS_LINE,
  SCORES_FAILED_LINE,
} from "./leagueSelectorContract";
import { PinnedLeagueList } from "./PinnedLeagueList";
import { togglePinnedLeague } from "./pinnedLeagues";
import { useLeaguePins } from "./useLeaguePins";

export type typeLeagueMenuDialogProps = {
  /** Driven by ?sheet=leagues, never by component state. */
  open: boolean;
  /** Clears ?sheet=. */
  onClose: () => void;
  /** Sets ?leagueId= and clears ?sheet=. Q277: a fan can tap a league to
   *  jump straight to that league's scores page. */
  onSelectLeague: (leagueId: string) => void;
};

export const LeagueMenuDialog = (
  props: typeLeagueMenuDialogProps,
): ReactNode => {
  const catalog = useLeagueCatalogGet({}, { enabled: props.open });
  // ISSUE-964: every pin, unpin and reorder goes through `useLeaguePins`, so
  // the row moves the moment it is clicked and a second click builds on the
  // first.
  const pins = useLeaguePins({ enabled: props.open });
  const profile = pins.profile;
  const pinnedLeagueIds = pins.pinnedLeagueIds;

  const view = buildLeagueMenuView({
    sports: catalog.data?.data?.sports ?? [],
    pinnedLeagueIds,
    isLoading: catalog.isLoading || profile.isLoading,
    isError:
      catalog.isError ||
      profile.isError ||
      Boolean(catalog.data?.error) ||
      Boolean(profile.data?.error),
  });

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={LEAGUE_MENU_TITLE}
      footer={<Button onClick={props.onClose}>Done</Button>}
    >
      <div data-testid="league-menu">
        {view.phase === "loading" ? (
          <div
            className="flex flex-col gap-2"
            data-testid="league-menu-loading"
          >
            <Skeleton shape="control" />
            <Skeleton shape="control" />
            <Skeleton shape="control" />
            <Skeleton shape="control" />
          </div>
        ) : null}

        {view.phase === "error" ? (
          <p data-testid="league-menu-error">{SCORES_FAILED_LINE}</p>
        ) : null}

        {view.phase === "empty" ? (
          <div data-testid="league-menu-empty">
            <p className="text-body-strong text-ink">{NO_LEAGUES_TITLE}</p>
            <p className="text-body text-ink-secondary">{NO_LEAGUES_BODY}</p>
          </div>
        ) : null}

        {view.phase === "ready" ? (
          <>
            {/* ISSUE-907 defect B, the pinned list clipping at both sheet edges.
                Measured, not argued: tests/e2e/src/app/leagueMenuGeometry.e2e.ts pins
                seven leagues at 390px and asserts that every pinned row's left edge,
                checkbox and first letter sit at or right of the scroll body's content
                box and that its last button ends at or left of it, in Chromium (the
                app project) and in WebKit with an iPhone 13 descriptor (the app-webkit
                project). WebKit emulation is not iOS Safari, so the card stays open
                until its iOS Safari device pass; see the card's plan.md.

                This section has no horizontal padding of its own and needs none.
                Tailwind's preflight zeroes padding on every element (pulled in by
                packages/theme/tailwind-preset.css:9), and the sheet's one horizontal
                rhythm is the px-4 on the Dialog scroll body at
                packages/ui/src/Dialog/Dialog.tsx:207, which this section inherits like
                every sibling.

                The "pinned list overflows the sheet" half that IS fixed is content
                height: the sheet no longer draws a pinned league twice, as this file's
                header explains. */}
            <section data-testid="league-menu-pinned">
              <h3 className="text-body-strong">{LEAGUE_MENU_PINNED_HEADING}</h3>
              {view.pinned.length === 0 ? (
                <p className="text-body text-ink-secondary">{NO_PINS_LINE}</p>
              ) : (
                <PinnedLeagueList
                  pinned={view.pinned}
                  applyPinChange={pins.applyPinChange}
                  onSelectLeague={props.onSelectLeague}
                />
              )}
            </section>

            {/* Only the leagues this fan has NOT pinned; the pinned ones are
                in the section above, once. `buildLeagueMenuView` also drops a
                sport left holding nothing, so a fan who pins every league in
                a sport does not read that sport's heading over an empty
                list. */}
            {view.sports.map((sport) => (
              <section
                key={sport.sportId}
                data-testid="league-menu-sport"
                data-sport-id={sport.sportId}
              >
                <h3 className="text-body-strong">{sport.heading}</h3>
                <ul>
                  {sport.leagues.map((league) => (
                    <LeagueRow
                      key={league.leagueId}
                      option={league}
                      isPinned={league.isPinned}
                      onTogglePin={() => {
                        pins.applyPinChange((current) =>
                          togglePinnedLeague({
                            pinnedLeagueIds: current,
                            leagueId: league.leagueId,
                          }),
                        );
                      }}
                      onOpen={() => {
                        props.onSelectLeague(league.leagueId);
                      }}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </>
        ) : null}
      </div>
    </Dialog>
  );
};
